import { Tabs } from 'antd'
import { useContext, useState } from 'react'
import dayjs from 'dayjs'
import { AuthenticationContext } from '../AuthenticationContext'
import NewInvoicingPageToggle from '../components/invoicing/NewInvoicingPageToggle'
import AllInvoicesView from '../components/invoicing/AllInvoices/AllInvoicesView'
import ReadyToInvoiceView from '../components/invoicing/ReadyToInvoice/ReadyToInvoiceView'
import BatchHistoryView from '../components/invoicing/BatchHistory/BatchHistoryView'
import BatchJobsDrawer from '../components/invoicing/BatchHistory/BatchJobsDrawer'
import { BatchMeta } from '../components/invoicing/BatchHistory/batchMeta'
import { BatchCreateRequest } from '../domain/invoicing/invoicingApi'

type InvoicingPageProps = {
  googleApiKey: string
  onNewPageToggle?: (useNewInvoicing: boolean) => void
}

type TabOption = 'batch-create' | 'batch-history' | 'invoices'

const InvoicingPage = ({ googleApiKey, onNewPageToggle }: InvoicingPageProps) => {
  const { currentUser } = useContext(AuthenticationContext)
  const [activeTab, setActiveTab] = useState<TabOption>('batch-create')
  const [drawerBatchId, setDrawerBatchId] = useState<string | null>(null)
  const [drawerBatchMeta, setDrawerBatchMeta] = useState<BatchMeta | undefined>(undefined)

  const handleBatchSuccess = (response: { batchId: string }, request: BatchCreateRequest) => {
    setDrawerBatchId(response.batchId)
    setDrawerBatchMeta({
      createdAt: dayjs().toISOString(),
      startDate: request.startDate,
      cutOffDate: request.cutOffDate,
      divisionGroups: request.divisionGroups,
      workAreaStatuses: request.workAreaStatuses,
      requestedByUserName: currentUser?.displayName ?? '',
      batchType: 'create',
    })
    setActiveTab('batch-history')
  }

  const handleBulkSendSuccess = (response: { batchId: string }) => {
    setDrawerBatchId(response.batchId)
    setDrawerBatchMeta({
      createdAt: dayjs().toISOString(),
      requestedByUserName: currentUser?.displayName ?? '',
      batchType: 'send',
    })
    setActiveTab('batch-history')
  }

  const handleBulkDeleteSuccess = (response: { batchId: string }) => {
    setDrawerBatchId(response.batchId)
    setDrawerBatchMeta({
      createdAt: dayjs().toISOString(),
      requestedByUserName: currentUser?.displayName ?? '',
      batchType: 'delete',
    })
    setActiveTab('batch-history')
  }

  const handleBulkSyncSuccess = (response: { batchId: string }) => {
    setDrawerBatchId(response.batchId)
    setDrawerBatchMeta({
      createdAt: dayjs().toISOString(),
      requestedByUserName: currentUser?.displayName ?? '',
      batchType: 'sync',
    })
    setActiveTab('batch-history')
  }

  const handleSelectBatch = (batchId: string, meta: BatchMeta) => {
    setDrawerBatchId(batchId)
    setDrawerBatchMeta(meta)
  }

  const handleCloseDrawer = () => {
    setDrawerBatchId(null)
    setDrawerBatchMeta(undefined)
  }

  return <div>
    <div style={{backgroundColor: 'white', padding: '8px 8px 0'}}>
    <NewInvoicingPageToggle useNewInvoicing={true} onToggle={onNewPageToggle} />
    <Tabs
      activeKey={activeTab}
      onChange={(key) => setActiveTab(key as TabOption)}
      items={[
        { key: 'batch-create', label: 'Jobs to Invoice' },
        { key: 'invoices', label: 'Invoices' },
        { key: 'batch-history', label: 'Batch History' },
      ]}
    />
    </div>

    {activeTab === 'batch-create' && (
      <ReadyToInvoiceView googleApiKey={googleApiKey} onBatchSuccess={handleBatchSuccess} />
    )}
    {activeTab === 'batch-history' && (
      <BatchHistoryView
        drawerBatchId={drawerBatchId}
        onSelectBatch={handleSelectBatch}
      />
    )}
    {activeTab === 'invoices' && (
      <AllInvoicesView
        onBulkSendSuccess={handleBulkSendSuccess}
        onBulkDeleteSuccess={handleBulkDeleteSuccess}
        onBulkSyncSuccess={handleBulkSyncSuccess}
      />
    )}

    <BatchJobsDrawer
      batchId={drawerBatchId}
      batchMeta={drawerBatchMeta}
      onClose={handleCloseDrawer}
    />
  </div>
}

export default InvoicingPage
